import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import { makeError } from "../../../utils";
import * as Yup from "yup";

const AddWarehouse = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [postLoading, setPostLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [warehouseUser, setWarehouseUser] = useState([]);
  const [roleRows, setRoleRows] = useState([{ role: "" }]); 
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    user_ids: [],
  });

  const schema = Yup.object().shape({
    name: Yup.string().required("Warehouse name is required"),
    address: Yup.string().required("Warehouse address is required"),
    user_ids: Yup.array()
      .min(1, "At least one user is required")
      .of(Yup.string().required()),
  });

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
    try {
      await schema.validateAt(name, updated);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const getWarehouseUser = async () => {
    try {
      const response = await request({
        url: "warehouse-users",
        method: "GET",
      });

      setWarehouseUser(response.data);
    } catch (error) {
      makeError(error, toast);
    }
  };

  useEffect(() => {
    getWarehouseUser();
  }, []);

  const handleRoleChange = async (index, value) => {
    const updated = [...roleRows];
    updated[index].role = value;

    setRoleRows(updated);

    const userIds = updated.map((row) => row.role).filter((id) => id !== "");

    const updatedFormData = { ...formData, user_ids: userIds };
    setFormData(updatedFormData);

    try {
      await schema.validateAt("user_ids", updatedFormData);
      setErrors((prev) => ({ ...prev, user_ids: "" }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, user_ids: error.message }));
    }
  };

  const addRoleRow = () => {
    setRoleRows([...roleRows, { role: "" }]);
  };

  const deleteRoleRow = (index) => {
    const deletedUserId = roleRows[index].role;

    const updatedRows = roleRows.filter((_, i) => i !== index);
    setRoleRows(updatedRows);

    const updatedUserIds = updatedRows
      .map((r) => r.role)
      .filter((id) => id !== "");

    setFormData((prev) => ({
      ...prev,
      user_ids: updatedUserIds,
    }));

    if (deletedUserId) {
      setWarehouseUser((prev) => [...prev]);
    }
  };

  const getFormData = () => {
    const fd = new FormData();
    fd.append("name", formData.name);
    fd.append("address", formData.address);

    formData.user_ids.forEach((id) => {
      fd.append("user_ids[]", id);
    });

    return fd;
  };

  const submit = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({}); 

      if (id) updateWarehouse();
      else addWarehouse();
    } catch (validationError) {
      const newErrors = {};
      validationError.inner.forEach((err) => {
        newErrors[err.path] = err.message;
      });
      setErrors(newErrors);
    }
  };

  const addWarehouse = async () => {
    try {
      setPostLoading(true);
      await request({
        url: "warehouses",
        method: "POST",
        data: getFormData(),
      });
      toast.success("Warehouse Added Successfully.");
      navigate('/warehouse-list');
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };

  const updateWarehouse = async () => {
    try {
      setPostLoading(true);
      await request({
        url: `warehouses/${id}`,
        method: "POST",
        data: getFormData(),
      });
      toast.success("Warehouse Updated Successfully.");
      navigate('/warehouse-list');
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };


const getWarehouseById = async () => {
  try {
    const response = await request({
      url: `warehouses/${id}`,
      method: "GET",
    });

    const data = response.data;
   const userIds = data.users?.map((u) => String(u.id)) || [];
    setFormData({
      name: data.name || "",
      address: data.address || "",
      user_ids: userIds,
    });

    setRoleRows(
      userIds.length
        ? userIds.map((id) => ({ role: id }))
        : [{ role: "" }]
    );
  } catch (error) {
    makeError(error, toast);
  }
};


  useEffect(()=>{
    if(id){
      getWarehouseById();
    }
  },[id])

  const handleCancel = () => {
    if (id) {
      navigate("/warehouse-list");
    } else {
      setFormData({
        name: "",
        address: "",
        user_ids: [],
      });
      setRoleRows([{ role: "" }])
      setErrors({});
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="container-fluid p-0">
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
          <div className="container-fluid">
            <div className="row g-3 mb-3 align-items-center">
              <div className="col">
                <ol className="breadcrumb bg-transparent mb-0">
                  <li className="breadcrumb-item">
                    <Link className="text-secondary" to="/hrms">
                      Home
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <span className="text-secondary">Warehouse</span>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Add
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-header">
                    <h6 className="card-title m-0">
                      {id ? "Update Warehouse" : "Add New Warehouse"}
                    </h6>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      {[
                        {
                          label: "Warehouse Name",
                          name: "name",
                          type: "text",
                          placeholder: "Enter warehouse name",
                        },
                        {
                          label: "Warehouse Address",
                          name: "address",
                          type: "text",
                          placeholder: "Enter warehouse address",
                        },
                      ].map((field) => (
                        <div
                          className="col-lg-6 col-md-6 col-sm-12"
                          key={field.name}
                        >
                          <label className="form-label fw-semibold">
                            {field.label} <span className="text-danger">*</span>
                          </label>
                          <input
                            type={field.type}
                            name={field.name}
                            className="form-control"
                            placeholder={field.placeholder}
                            value={formData[field.name]}
                            onChange={handleInputChange}
                          />
                          {errors[field.name] && (
                            <div className="text-danger mt-1">
                              {errors[field.name]}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="row mt-4">
                      <label className="form-label fw-semibold">
                        Add User <span className="text-danger">*</span>
                      </label>

                      {roleRows.map((row, index) => {
                        const selectedUsers = formData.user_ids;

                        const filteredUsers = warehouseUser.filter(
                          (u) =>
                            !selectedUsers.includes(String(u.id)) ||
                            row.role === String(u.id)
                        );

                        return (
                          <div
                            className="col-lg-6 col-md-6 col-sm-12 d-flex align-items-center mb-2"
                            key={index}
                          >
                            <select
                              className="form-select"
                              value={row.role}
                              onChange={(e) =>
                                handleRoleChange(index, e.target.value)
                              }
                            >
                              <option value="">Select User</option>
                              {filteredUsers.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.label}
                                </option>
                              ))}
                            </select>

                            {roleRows.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-danger ms-2"
                                onClick={() => deleteRoleRow(index)}
                              >
                                Delete
                              </button>
                            )}

                            {index === roleRows.length - 1 && (
                              <button
                                type="button"
                                className="btn btn-success ms-2"
                                onClick={addRoleRow}
                              >
                                +
                              </button>
                            )}
                          </div>
                        );
                      })}

                      {errors.user_ids && (
                        <div className="text-danger mt-1">{errors.user_ids}</div>
                      )}
                    </div>
                  </div>

                  <div className="card-footer">
                    <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary ms-3"
                      onClick={submit}
                      disabled={postLoading}
                    >
                      {postLoading
                        ? "Saving..."
                        : id
                        ? "Update Warehouse"
                        : "Add Warehouse"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </>
  );
};

export default AddWarehouse;
